// src/pages/Inventory/StockLevels.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '@/lib/axios';
import {
  FaDownload, FaFilePdf, FaSyncAlt, FaSearch, FaMapMarkerAlt, FaExclamationTriangle,
  FaChevronDown, FaChevronUp, FaMapPin, FaBolt, FaBan, FaRecycle
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

/* ----------------------------- utils ----------------------------- */
const money = (n) =>
  n == null ? '—' : Number(n).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const safeBtoa = (obj) => {
  try { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))); }
  catch { return ''; }
};

const LowBadge = () => (
  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 text-[10px] uppercase tracking-wide">
    Low
  </span>
);

const SourceBadge = ({ source }) => {
  if (!source) return null;
  const s = String(source).toLowerCase();
  const key = s.split(':')[0];
  const styles = {
    balances: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    movements: 'bg-blue-100 text-blue-700 border-blue-200',
    legacy: 'bg-amber-100 text-amber-700 border-amber-200',
    items: 'bg-slate-100 text-slate-700 border-slate-200',
    empty: 'bg-rose-100 text-rose-700 border-rose-200',
  };
  const label =
    key === 'balances' ? 'Balances' :
    key === 'movements' ? 'Movements' :
    key === 'legacy' ? 'Legacy' :
    key === 'empty' ? 'Empty' :
    'Items';

  return (
    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded border text-[10px] ${styles[key] || styles.items}`}>
      src: {label}
    </span>
  );
};

/* ============================== PAGE ============================== */
export default function StockLevelsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState('');

  const [q, setQ] = useState('');
  const dq = useDebounced(q, 350);

  const [locationId, setLocationId] = useState('');
  const [locations, setLocations] = useState([]);

  const [showOnlyLowStock, setShowOnlyLowStock] = useState(false);

  // Wastage toggles
  const [includeWastage, setIncludeWastage] = useState(false);
  const [hideWastageInBreakdown, setHideWastageInBreakdown] = useState(false);

  const [sort, setSort] = useState('item_code');
  const [dir, setDir] = useState('asc');

  const [selected, setSelected] = useState(() => new Set());

  const [openBreakdownFor, setOpenBreakdownFor] = useState(null);
  const breakdownCacheRef = useRef(new Map());
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState('');

  const [sourceHdr, setSourceHdr] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const [autoRefresh, setAutoRefresh] = useState(true);
  const POLL_MS = 20000;

  const headerCheckboxRef = useRef(null);
  const navigate = useNavigate();

  // NEW: request cancellation to prevent “flash/race”
  const inFlightRef = useRef(null);

  // NEW: UI fallback when chosen location has no balances yet
  const [locationFallbackActive, setLocationFallbackActive] = useState(false);
  const [locationFallbackReason, setLocationFallbackReason] = useState('');

  /* ------------------------- load locations ------------------------ */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/inventory/locations', { params: { active_only: 1 } });
        if (!cancelled) setLocations(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setLocations([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Default the location once locations are loaded
  useEffect(() => {
    if (!locations.length || locationId) return;
    const preferred =
      locations.find(l => l.is_default_goods_received) ||
      locations.find(l => /main/i.test(String(l.name))) ||
      locations[0];
    if (preferred?.location_id != null) setLocationId(String(preferred.location_id));
  }, [locations, locationId]);

  /* ---------------------- normalize backend row -------------------- */
  const normalizeRow = (r) => {
    const on_hand_qty = Number(r.on_hand_qty ?? r.quantity ?? r.on_hand ?? r.current_stock ?? 0);
    const avg_cost =
      r.avg_cost != null ? Number(r.avg_cost)
      : r.cost != null ? Number(r.cost)
      : r.unit_price != null ? Number(r.unit_price)
      : null;
    const rop = r.reorder_point ?? r.par_level ?? null;

    const reorder_qty =
      r.reorder_qty != null ? Number(r.reorder_qty)
      : rop != null ? Math.max(Number(rop) - on_hand_qty, 0)
      : null;

    const can_issue = r.can_issue != null ? Boolean(r.can_issue) : on_hand_qty > 0;
    const can_transfer = r.can_transfer != null ? Boolean(r.can_transfer) : on_hand_qty > 0;

    const is_below_rop =
      r.is_below_rop != null ? Boolean(r.is_below_rop) : (rop != null && Number(on_hand_qty) < Number(rop));

    const value = r.value != null ? Number(r.value) : (avg_cost != null ? on_hand_qty * avg_cost : null);

    return {
      item_id: r.item_id ?? r.stock_item_id ?? r.id,
      item_code: r.item_code ?? r.code ?? '',
      description: r.description ?? r.stock_name ?? '',
      unit_size: r.unit_size ?? r.pack_size ?? null,
      uom: r.uom ?? r.unit ?? r.unit_name ?? null,
      location_id: r.location_id ?? null,
      location_name: r.location_name ?? r.bin ?? r.location ?? (locationId ? 'Selected location' : null),
      vendor_id: r.vendor_id ?? null,
      vendor_name: r.vendor_name ?? null,
      on_hand_qty,
      avg_cost,
      value,
      reorder_point: rop,
      reorder_qty,
      can_issue,
      can_transfer,
      is_below_rop,
    };
  };

  /* ------------------------ fetch stock levels --------------------- */
  const fetchStockLevels = React.useCallback(async ({ setBusy = true } = {}) => {
    try { inFlightRef.current?.abort?.(); } catch {}
    const controller = new AbortController();
    inFlightRef.current = controller;

    if (setBusy) setLoading(true);
    setError('');

    const runFetch = async (overrideParams = {}) => {
      const params = {};
      if (dq.trim()) params.q = dq.trim();
      if (locationId) params.location_id = Number(locationId);
      if (sort) params.sort = sort;
      if (dir) params.dir = dir;
      if (includeWastage) params.include_wastage = 1;

      Object.assign(params, overrideParams);

      const res = await api.get('/inventory/stock-levels', { params, signal: controller.signal });

      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.rows)
        ? res.data.rows
        : Array.isArray(res.data?.items)
        ? res.data.items
        : Array.isArray(res.data?.data)
        ? res.data.data
        : [];

      return { res, list };
    };

    try {
      const { res, list } = await runFetch();

      setSourceHdr(res.headers?.['x-stock-levels-source'] || '');
      setLastUpdatedAt(new Date().toISOString());

      if (locationId && Array.isArray(list) && list.length === 0) {
        try {
          const { res: resAll, list: listAll } = await runFetch({ location_id: undefined });
          if (Array.isArray(listAll) && listAll.length > 0) {
            setLocationFallbackActive(true);
            setLocationFallbackReason(
              `No balances exist for location #${locationId} yet. Showing All Locations temporarily (stock exists elsewhere).`
            );

            setSourceHdr(resAll.headers?.['x-stock-levels-source'] || '');
            const normalizedAll = listAll.map(normalizeRow);
            setRows(normalizedAll);

            setSelected((prev) => {
              const ids = new Set(normalizedAll.map((r) => r.item_id));
              const next = new Set();
              prev.forEach((id) => ids.has(id) && next.add(id));
              return next;
            });

            if (openBreakdownFor && !normalizedAll.some((r) => r.item_id === openBreakdownFor)) {
              setOpenBreakdownFor(null);
            }
            return;
          }
        } catch {}
      }

      setLocationFallbackActive(false);
      setLocationFallbackReason('');

      const normalized = list.map(normalizeRow);
      setRows(normalized);

      setSelected((prev) => {
        const ids = new Set(normalized.map((r) => r.item_id));
        const next = new Set();
        prev.forEach((id) => ids.has(id) && next.add(id));
        return next;
      });

      if (openBreakdownFor && !normalized.some((r) => r.item_id === openBreakdownFor)) {
        setOpenBreakdownFor(null);
      }
    } catch (err) {
      if (err?.name !== 'CanceledError' && err?.name !== 'AbortError') {
        console.error('Failed to fetch stock levels', err);
        setError(err?.response?.data?.error || 'Failed to fetch stock levels.');
        setRows([]);
        setSelected(new Set());
      }
    } finally {
      if (setBusy) setLoading(false);
    }
  }, [dq, locationId, sort, dir, openBreakdownFor, includeWastage]);

  useEffect(() => {
    fetchStockLevels({ setBusy: true });
    return () => {
      try { inFlightRef.current?.abort?.(); } catch {}
    };
  }, [fetchStockLevels]);

  /* --------------------------- refresh ops ------------------------- */
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchStockLevels({ setBusy: false });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!autoRefresh) return;
    let t;
    const tick = async () => {
      if (document.visibilityState === 'visible' && !refreshing && !loading) {
        await fetchStockLevels({ setBusy: false });
      }
      t = setTimeout(tick, POLL_MS);
    };
    t = setTimeout(tick, POLL_MS);
    const onFocus = () => fetchStockLevels({ setBusy: false });
    window.addEventListener('focus', onFocus);
    return () => {
      clearTimeout(t);
      window.removeEventListener('focus', onFocus);
    };
  }, [autoRefresh, refreshing, loading, fetchStockLevels]);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e?.key) return;
      if (e.key === 'inventory:pulse') {
        fetchStockLevels({ setBusy: false });
        return;
      }
      if (e.key.startsWith('inventory:breakdown:invalidate:')) {
        const idStr = e.key.split(':').pop();
        const id = parseInt(idStr, 10);
        if (Number.isInteger(id)) {
          breakdownCacheRef.current.delete(id);
          if (openBreakdownFor === id) setOpenBreakdownFor(null);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchStockLevels, openBreakdownFor]);

  /* ----------------------- reordering helpers ---------------------- */
  const navigateToSinglePO = (row) => navigate(`/procurement/new-po?prefill_item_id=${row.item_id}`);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    return showOnlyLowStock ? rows.filter((r) => r.is_below_rop) : rows;
  }, [rows, showOnlyLowStock]);

  const vendorGroups = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const key = r.vendor_id ?? 'unassigned';
      if (!map.has(key)) {
        map.set(key, {
          vendor_id: r.vendor_id ?? null,
          vendor_name: r.vendor_name ?? '— Unassigned —',
          rows: [],
          subtotalValue: 0,
          anyLow: false,
        });
      }
      const g = map.get(key);
      g.rows.push(r);
      g.subtotalValue += Number.isFinite(Number(r.value)) ? Number(r.value) : 0;
      g.anyLow = g.anyLow || r.is_below_rop;
    }
    const groups = Array.from(map.values());
    return groups.sort((a, b) => {
      if (a.vendor_id && !b.vendor_id) return -1;
      if (!a.vendor_id && b.vendor_id) return 1;
      return (a.vendor_name || '').localeCompare(b.vendor_name || '');
    });
  }, [filtered]);

  useEffect(() => {
    const box = headerCheckboxRef.current;
    if (!box) return;
    if (filtered.length === 0) {
      box.checked = false;
      box.indeterminate = false;
      return;
    }
    const selectedCount = filtered.filter((r) => selected.has(r.item_id)).length;
    box.checked = selectedCount === filtered.length;
    box.indeterminate = selectedCount > 0 && selectedCount < filtered.length;
  }, [filtered, selected]);

  const toggleAllFiltered = () => {
    const ids = filtered.map((r) => r.item_id);
    const setAll = filtered.some((r) => !selected.has(r.item_id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (setAll) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const toggleGroup = (group) => {
    const ids = group.rows.map((r) => r.item_id);
    const setAll = group.rows.some((r) => !selected.has(r.item_id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (setAll) ids.forEach((id) => next.add(id));
      else ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const groupLocally = (chosen) => {
    const groupsMap = new Map();
    const unassigned = [];
    for (const r of chosen) {
      const qty = Number(r.reorder_qty);
      const quantity = Number.isFinite(qty) && qty > 0 ? qty : 1;
      if (!r.vendor_id) {
        unassigned.push({ item_code: r.item_code, quantity });
        continue;
      }
      const key = String(r.vendor_id);
      if (!groupsMap.has(key)) groupsMap.set(key, { vendor_id: r.vendor_id, vendor_name: r.vendor_name || null, items: [] });
      groupsMap.get(key).items.push({ item_id: r.item_id, quantity });
    }
    return { groups: Array.from(groupsMap.values()), unassigned };
  };

  const handleReorderVendor = (group) => {
    if (!group.vendor_id) {
      alert('This group has no vendor linked. Link items to a vendor first.');
      return;
    }
    const chosen = group.rows.filter((r) => selected.has(r.item_id));
    const useRows = chosen.length ? chosen : group.rows;
    const items = useRows.map((r) => ({ item_id: r.item_id, quantity: Number(r.reorder_qty) > 0 ? Number(r.reorder_qty) : 1 }));
    const encoded = safeBtoa([{ vendor_id: group.vendor_id, vendor_name: group.vendor_name || null, items }]);
    if (!encoded) { alert('Failed to encode selection for prefill.'); return; }
    navigate(`/procurement/new-po?bulk_from_stock=1&groups=${encodeURIComponent(encoded)}`);
  };

  const handleBulkGroupedReorder = async () => {
    const chosen = filtered.filter((r) => selected.has(r.item_id));
    if (!chosen.length) { alert('Select at least one item to reorder.'); return; }
    setBulkBusy(true);
    try {
      const ids = [...new Set(chosen.map((r) => r.item_id))];
      try {
        const { data } = await api.post('/inventory/stock-levels/reorder-groups', { item_ids: ids });
        const idByCode = new Map(chosen.map((r) => [String(r.item_code), r.item_id]));
        const groups = (Array.isArray(data?.groups) ? data.groups : []).map((g) => ({
          vendor_id: g.vendor_id,
          vendor_name: g.vendor_name ?? null,
          items: (g.items || []).map((it) => ({ item_id: it.item_id ?? idByCode.get(String(it.item_code)), quantity: Number(it.quantity) || 1 })),
        }));
        const unassigned = (Array.isArray(data?.unassigned) ? data.unassigned : []).map((u) => ({ item_code: u.item_code ?? null, quantity: Number(u.quantity) || 1 }));
        const sanitized = groups
          .map((g) => ({ ...g, items: (g.items || []).filter((it) => Number.isFinite(Number(it.item_id))) }))
          .filter((g) => g.vendor_id && g.items.length > 0);
        if (unassigned?.length) {
          const sample = unassigned.slice(0, 5).map((x) => `• ${x.item_code || 'unlinked'} (qty ${x.quantity})`).join('\n');
          const more = unassigned.length > 5 ? `\n…and ${unassigned.length - 5} more` : '';
          const proceed = confirm(`Some selected items do not have a linked vendor:\n\n${sample}${more}\n\nContinue with grouped POs for linked items?`);
          if (!proceed) return;
        }
        const encoded = safeBtoa(sanitized);
        if (!encoded || !sanitized.length) { alert('No vendor-linked items in your selection to prefill.'); return; }
        navigate(`/procurement/new-po?bulk_from_stock=1&groups=${encodeURIComponent(encoded)}`);
      } catch {
        const { groups, unassigned } = groupLocally(chosen);
        if (unassigned.length) {
          const sample = unassigned.slice(0, 5).map((x) => `• ${x.item_code} (qty ${x.quantity})`).join('\n');
          const more = unassigned.length > 5 ? `\n…and ${unassigned.length - 5} more` : '';
          const proceed = confirm(`Some selected items do not have a linked vendor:\n\n${sample}${more}\n\nContinue with grouped POs for linked items?`);
          if (!proceed) return;
        }
        const encoded = safeBtoa(groups);
        if (!encoded || !groups.length) { alert('No vendor-linked items in your selection to prefill.'); return; }
        navigate(`/procurement/new-po?bulk_from_stock=1&groups=${encodeURIComponent(encoded)}`);
      }
    } finally {
      setBulkBusy(false);
    }
  };

  /* --------------------------- breakdown --------------------------- */
  const toggleBreakdown = async (item) => {
    setBreakdownError('');
    if (openBreakdownFor === item.item_id) { setOpenBreakdownFor(null); return; }

    if (!breakdownCacheRef.current.has(item.item_id)) {
      setBreakdownLoading(true);
      try {
        const params = item.item_id ? { item_id: item.item_id } : { item_code: item.item_code };
        if (hideWastageInBreakdown) params.exclude_wastage = 1;
        const { data } = await api.get('/inventory/stock-levels/breakdown', { params });
        breakdownCacheRef.current.set(item.item_id, Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('breakdown error', err);
        setBreakdownError(err?.response?.data?.error || 'Failed to load location breakdown.');
      } finally {
        setBreakdownLoading(false);
      }
    }

    setOpenBreakdownFor(item.item_id);
  };

  /* ------------------------------ UI data -------------------------- */
  const selectedInFiltered = filtered.filter((r) => selected.has(r.item_id)).length;
  const toggleSort = (key) => { sort === key ? setDir((d) => (d === 'asc' ? 'desc' : 'asc')) : (setSort(key), setDir('asc')); };
  const sortIndicator = (key) => (sort === key ? (dir === 'asc' ? ' ▲' : ' ▼') : '');
  const currentLocationName = useMemo(
    () => locations.find(x => String(x.location_id) === String(locationId))?.name || null,
    [locationId, locations]
  );

  const totalValue = useMemo(
    () => filtered.reduce((acc, r) => acc + (Number.isFinite(Number(r.value)) ? Number(r.value) : 0), 0),
    [filtered]
  );
  const totalUnits = useMemo(
    () => filtered.reduce((acc, r) => acc + (Number.isFinite(Number(r.on_hand_qty)) ? Number(r.on_hand_qty) : 0), 0),
    [filtered]
  );

  /* ------------------------------ export --------------------------- */
  const exportCSV = React.useCallback(() => {
    const header = ['Item ID','Item Code','Description','Vendor','Location','UoM','On-Hand','Avg Cost','Value','Reorder Pt','Reorder Qty (Suggested)','Selected','Low?','Can Issue?','Can Transfer?'];
    const body = filtered.map((r) => [
      r.item_id ?? '', r.item_code ?? '', r.description ?? '', r.vendor_name ?? '',
      r.location_name ?? '', r.uom ?? '',
      r.on_hand_qty ?? '', r.avg_cost ?? '', r.value ?? '', r.reorder_point ?? '', r.reorder_qty ?? '',
      selected.has(r.item_id) ? 'yes' : 'no',
      r.is_below_rop ? 'yes' : 'no',
      r.can_issue ? 'yes' : 'no',
      r.can_transfer ? 'yes' : 'no',
    ]);
    const csv = [header, ...body].map((row) => row.map((f) => `"${String(f ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    saveAs(new Blob([csv], { type: 'text/csv; charset=utf-8' }), 'stock_levels.csv');
  }, [filtered, selected]);

  const exportPDF = React.useCallback(() => {
    const doc = new jsPDF();
    doc.text('Stock Levels Report', 14, 16);
    autoTable(doc, {
      startY: 20,
      head: [['Item Code','Description','Vendor','Loc','UoM','On-Hand','Avg Cost','Value','ROP','ROQ','Low?']],
      body: filtered.map((r) => [
        r.item_code ?? '', r.description ?? '', r.vendor_name ?? '', r.location_name ?? '',
        r.uom ?? '', r.on_hand_qty ?? '', r.avg_cost != null ? money(r.avg_cost) : '—',
        r.value != null ? money(r.value) : '—', r.reorder_point ?? '', r.reorder_qty ?? '', r.is_below_rop ? 'Yes' : 'No',
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [34, 34, 34] },
    });
    doc.save('stock_levels.pdf');
  }, [filtered]);

  /* ------------------------------ render --------------------------- */
  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 via-white to-sky-50 text-slate-900">
      {/* header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <h1 className="text-3xl font-bold">
          📦 Stock Levels
          <SourceBadge source={sourceHdr} />
        </h1>

        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search items…"
              className="pl-9 pr-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>

          <div className="relative">
            <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              className="pl-9 pr-3 py-2 rounded-lg bg-white border border-slate-200 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
              title="Filter by location"
            >
              <option value="">All locations</option>
              {locations.map((loc) => (
                <option key={loc.location_id ?? loc.name} value={loc.location_id}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setShowOnlyLowStock((s) => !s)}
            className={`px-4 py-2 rounded-lg text-sm flex items-center gap-2 shadow-sm border ${
              showOnlyLowStock
                ? 'bg-amber-600 text-white border-amber-600'
                : 'bg-amber-500 hover:bg-amber-600 text-white border-amber-500'
            }`}
            title="Toggle below reorder"
          >
            <FaExclamationTriangle />
            {showOnlyLowStock ? 'Show All' : 'Only Low Stock'}
          </button>

          <button
            onClick={() => setIncludeWastage((v) => !v)}
            className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 border shadow-sm ${
              includeWastage
                ? 'bg-white border-slate-200 text-slate-700'
                : 'bg-amber-50 border-amber-200 text-amber-700'
            }`}
            title="Toggle including Wastage/Damaged/Expired in totals"
          >
            {includeWastage ? <FaRecycle /> : <FaBan />}
            {includeWastage ? 'Include Wastage' : 'Exclude Wastage'}
          </button>

          <button
            onClick={() => setHideWastageInBreakdown((v) => !v)}
            className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 border shadow-sm ${
              hideWastageInBreakdown
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-white border-slate-200 text-slate-700'
            }`}
            title="Hide wastage lines in location breakdowns"
          >
            <FaBan />
            {hideWastageInBreakdown ? 'Hide Wastage in Breakdown' : 'Show Wastage in Breakdown'}
          </button>

          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 border shadow-sm ${
              autoRefresh
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-white border-slate-200 text-slate-700'
            }`}
            title="Auto refresh while this tab is visible"
          >
            <FaBolt /> {autoRefresh ? 'Auto On' : 'Auto Off'}
          </button>

          <button
            onClick={exportCSV}
            className="bg-emerald-600 hover:bg-emerald-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 text-white shadow-sm"
          >
            <FaDownload /> CSV
          </button>

          <button
            onClick={exportPDF}
            className="bg-rose-600 hover:bg-rose-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 text-white shadow-sm"
          >
            <FaFilePdf /> PDF
          </button>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-sky-600 hover:bg-sky-700 px-4 py-2 rounded-lg text-sm flex items-center gap-2 disabled:opacity-60 text-white shadow-sm"
          >
            <FaSyncAlt className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {locationFallbackActive && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm px-3 py-2 shadow-sm">
          ⚠️ {locationFallbackReason}
        </div>
      )}

      {/* topline KPIs */}
      <div className="flex flex-wrap items-center gap-4 mb-3 text-sm text-slate-600">
        <div>
          {filtered.length.toLocaleString('en-GB')} item(s) · {selectedInFiltered} selected
          {currentLocationName && (
            <span className="ml-2 inline-flex items-center gap-1 text-slate-500">
              <FaMapPin /> {currentLocationName}
            </span>
          )}
        </div>
        <div>On-hand Units: <span className="text-slate-900 font-medium">{totalUnits.toLocaleString('en-GB')}</span></div>
        <div>Total Value: <span className="text-emerald-700 font-semibold">{money(totalValue)}</span></div>
        {!includeWastage && <div className="text-xs text-amber-700">Wastage/Damaged/Expired excluded from totals</div>}
        {lastUpdatedAt && <div className="text-xs text-slate-500">Updated {new Date(lastUpdatedAt).toLocaleTimeString('en-GB')}</div>}
      </div>

      {/* bulk actions */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-xs text-slate-500" />
        <div className="flex items-center gap-2">
          <button
            onClick={handleBulkGroupedReorder}
            className="bg-violet-600 hover:bg-violet-700 px-3 py-2 rounded-lg text-sm disabled:opacity-50 inline-flex items-center gap-2 text-white shadow-sm"
            disabled={selectedInFiltered === 0 || bulkBusy}
            title="Create grouped PO prefill per vendor"
          >
            {bulkBusy && <FaSyncAlt className="animate-spin" />} Reorder Selected (Grouped)
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="bg-slate-200 hover:bg-slate-300 text-slate-800 px-3 py-2 rounded-lg text-sm disabled:opacity-50 shadow-sm"
            disabled={selected.size === 0}
            title="Clear selection"
          >
            Clear Selection
          </button>
        </div>
      </div>

      {error && <div className="mb-4 text-sm text-rose-600">{error}</div>}

      {/* grouped table by vendor */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full table-auto text-sm">
          <thead>
            <tr className="bg-slate-100 text-left text-slate-700">
              <th className="p-3 border-b border-slate-200 w-10">
                <input ref={headerCheckboxRef} type="checkbox" onChange={toggleAllFiltered} aria-label="Select all" />
              </th>
              <th className="p-3 border-b border-slate-200 cursor-pointer select-none" onClick={() => toggleSort('item_code')} title="Sort by Item Code">
                Item Code{sortIndicator('item_code')}
              </th>
              <th className="p-3 border-b border-slate-200 cursor-pointer select-none" onClick={() => toggleSort('description')} title="Sort by Description">
                Description{sortIndicator('description')}
              </th>
              <th className="p-3 border-b border-slate-200 cursor-pointer select-none" onClick={() => toggleSort('vendor_name')} title="Sort by Vendor">
                Vendor{sortIndicator('vendor_name')}
              </th>
              <th className="p-3 border-b border-slate-200">Location</th>
              <th className="p-3 border-b border-slate-200">UoM</th>
              <th className="p-3 border-b border-slate-200 text-right cursor-pointer select-none" onClick={() => toggleSort('on_hand_qty')} title="Sort by On-Hand">
                On-Hand{sortIndicator('on_hand_qty')}
              </th>
              <th className="p-3 border-b border-slate-200 text-right cursor-pointer select-none" onClick={() => toggleSort('avg_cost')} title="Sort by Avg Cost">
                Avg Cost{sortIndicator('avg_cost')}
              </th>
              <th className="p-3 border-b border-slate-200 text-right cursor-pointer select-none" onClick={() => toggleSort('value')} title="Sort by Value">
                Value{sortIndicator('value')}
              </th>
              <th className="p-3 border-b border-slate-200 text-right cursor-pointer select-none" onClick={() => toggleSort('reorder_point')} title="Sort by Reorder Point">
                Reorder Pt{sortIndicator('reorder_point')}
              </th>
              <th className="p-3 border-b border-slate-200 text-right cursor-pointer select-none" onClick={() => toggleSort('reorder_qty')} title="Sort by Suggested Qty">
                Suggested Qty{sortIndicator('reorder_qty')}
              </th>
              <th className="p-3 border-b border-slate-200 text-right">PO</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td className="p-6 text-center text-slate-500" colSpan={12}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td className="p-6 text-center text-slate-500" colSpan={12}>No stock items found.</td></tr>
            ) : (
              vendorGroups.map((group) => {
                const selectedCount = group.rows.filter((r) => selected.has(r.item_id)).length;
                const allChecked = selectedCount === group.rows.length && group.rows.length > 0;
                const indeterminate = selectedCount > 0 && selectedCount < group.rows.length;

                return (
                  <React.Fragment key={group.vendor_id ?? 'unassigned'}>
                    {/* group header */}
                    <tr className="bg-slate-50">
                      <td className="p-3 border-b border-slate-200">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          ref={(el) => { if (el) el.indeterminate = indeterminate; }}
                          onChange={() => toggleGroup(group)}
                          aria-label={`select group ${group.vendor_name}`}
                        />
                      </td>
                      <td className="p-3 border-b border-slate-200 font-semibold text-slate-900" colSpan={8}>
                        {group.vendor_name}
                        <span className="ml-2 text-xs text-slate-500">
                          ({group.rows.length} item{group.rows.length !== 1 ? 's' : ''})
                        </span>
                        <span className="ml-3 text-xs text-slate-600">
                          Total Value: {money(group.subtotalValue)}
                        </span>
                        {group.anyLow && <span className="ml-3 text-xs text-rose-600">• Low stock present</span>}
                      </td>
                      <td className="p-3 border-b border-slate-200 text-right" colSpan={3}>
                        <button
                          onClick={() => handleReorderVendor(group)}
                          disabled={!group.vendor_id}
                          className="bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white px-3 py-1 rounded text-xs shadow-sm"
                          title={group.vendor_id ? 'Create PO for this vendor' : 'Link items to a vendor to enable grouped reorder'}
                        >
                          Reorder this Vendor
                        </button>
                      </td>
                    </tr>

                    {/* rows */}
                    {group.rows.map((r) => {
                      const open = openBreakdownFor === r.item_id;
                      const bd = breakdownCacheRef.current.get(r.item_id) || [];
                      return (
                        <React.Fragment key={`${r.item_id}-${r.item_code}-${r.location_name || 'all'}`}>
                          <tr className={`hover:bg-sky-50 ${r.is_below_rop ? 'bg-rose-50' : 'bg-white'}`}>
                            <td className="p-3 border-b border-slate-200">
                              <input type="checkbox" checked={selected.has(r.item_id)} onChange={() => toggleOne(r.item_id)} aria-label={`select ${r.item_code}`} />
                            </td>
                            <td className="p-3 border-b border-slate-200">
                              <span className="font-mono text-slate-900">{r.item_code || '—'}</span>
                              {r.is_below_rop && <LowBadge />}
                            </td>
                            <td className="p-3 border-b border-slate-200 text-slate-700">{r.description || '—'}</td>
                            <td className="p-3 border-b border-slate-200 text-slate-700">{r.vendor_name || '—'}</td>
                            <td className="p-3 border-b border-slate-200 text-slate-700">
                              {r.location_name || (locationId ? 'Selected location' : '—')}
                              <button
                                onClick={() => toggleBreakdown(r)}
                                className="ml-2 text-xs px-2 py-1 rounded border border-slate-200 hover:bg-slate-100 text-slate-700"
                                title="Show per-location balances"
                              >
                                {open ? <span className="inline-flex items-center gap-1"><FaChevronUp /> Hide</span>
                                      : <span className="inline-flex items-center gap-1"><FaChevronDown /> Breakdown</span>}
                              </button>
                            </td>
                            <td className="p-3 border-b border-slate-200 text-slate-700">{r.uom || r.unit_size || '—'}</td>
                            <td className="p-3 border-b border-slate-200 text-right">
                              <span className={r.is_below_rop ? 'text-rose-600 font-semibold' : 'text-slate-900'}>
                                {r.on_hand_qty != null ? Number(r.on_hand_qty).toLocaleString('en-GB') : '—'}
                              </span>
                            </td>
                            <td className="p-3 border-b border-slate-200 text-right text-slate-700">{money(r.avg_cost)}</td>
                            <td className="p-3 border-b border-slate-200 text-right text-slate-700">{money(r.value)}</td>
                            <td className="p-3 border-b border-slate-200 text-right text-slate-700">{r.reorder_point ?? '—'}</td>
                            <td className="p-3 border-b border-slate-200 text-right text-slate-700">{r.reorder_qty ?? '—'}</td>
                            <td className="p-3 border-b border-slate-200 text-right">
                              <button
                                onClick={() => navigateToSinglePO(r)}
                                className="bg-violet-600 hover:bg-violet-700 text-white px-3 py-1 rounded text-xs shadow-sm"
                                title="Reorder this item"
                              >
                                Reorder
                              </button>
                            </td>
                          </tr>

                          {/* inline breakdown */}
                          {open && (
                            <tr>
                              <td colSpan={12} className="bg-slate-50 border-b border-slate-200">
                                <div className="p-3">
                                  {breakdownLoading ? (
                                    <div className="text-sm text-slate-500">Loading breakdown…</div>
                                  ) : breakdownError ? (
                                    <div className="text-sm text-rose-600">{breakdownError}</div>
                                  ) : bd.length === 0 ? (
                                    <div className="text-sm text-slate-500">No locations found.</div>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-[420px] text-sm">
                                        <thead>
                                          <tr className="text-left text-slate-600">
                                            <th className="py-1 pr-4">Location</th>
                                            <th className="py-1 pr-4 text-right">On-Hand</th>
                                            <th className="py-1 pr-4">Can Issue?</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {bd.map((b) => (
                                            <tr key={b.location_id ?? b.location_name}>
                                              <td className="py-1 pr-4 text-slate-800">{b.location_name || '—'}</td>
                                              <td className="py-1 pr-4 text-right text-slate-800">
                                                {Number(b.on_hand_qty ?? 0).toLocaleString('en-GB')}
                                              </td>
                                              <td className="py-1 pr-4 text-slate-700">{b.can_issue ? 'Yes' : 'No'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}